import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaCircleCheck,
  FaArrowRight,
  FaBolt,
  FaClock,
} from "react-icons/fa6";
import { FaInfoCircle } from "react-icons/fa";
import {
  buttonStyleSm2,
  buttonStyleSecondarySm22,
  formatCurrency,
} from "../../help/Utils";
import {
  getApplication,
  setUscisFeeDecision,
  getUscisFeeQuestionnaire,
  submitUscisFeeQuestionnaire,
  UscisFeeDecisionResponse,
} from "../../API/applicationAPI";
import {
  Application,
  UscisFeeQuestionnaire as UscisFeeQuestionnaireType,
  UscisFeeQuestion,
  UscisFeeAnswer,
  UscisFeeComputation,
} from "../../types/application";
import { createCheckoutSession } from "../../API/paymentAPI";
import PovertyGuidelineHelp from "./PovertyGuidelineHelp";
import { getPackageById, Package as SelectedPackage } from "../../API/packageAPI";
import ButtonLoadingLabel from "../../components/ButtonLoadingLabel";

const UscisFeeDecision: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [application, setApplication] = useState<Application | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [questionnaire, setQuestionnaire] =
    useState<UscisFeeQuestionnaireType | null>(null);
  const [answers, setAnswers] = useState<
    Record<string, boolean | string | number>
  >({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [feeComputation, setFeeComputation] =
    useState<UscisFeeComputation | null>(null);
  const [loadingFee, setLoadingFee] = useState(false);
  const [selectedPackage, setSelectedPackage] =
    useState<SelectedPackage | null>(null);
  const [packageLoading, setPackageLoading] = useState(false);

  // Use ref to prevent multiple simultaneous API calls
  const fetchingRef = useRef(false);
  const questionnaireFetchedRef = useRef(false);
  const payLaterProcessingRef = useRef(false);

  const packageFee =
    !packageLoading && selectedPackage?.price != null
      ? Number(selectedPackage.price)
      : null;

  const getUscisFeeAmount = (): number | null => {
    if (feeComputation) {
      return feeComputation.breakdown?.total ?? feeComputation.amount ?? null;
    }

    if (application?.uscisFee) {
      const { breakdown, amount } = application.uscisFee;
      const computedFee = breakdown?.total ?? amount;
      if (computedFee !== null && computedFee !== undefined) {
        return computedFee;
      }
    }

    return null;
  };

  const uscisFeeAmount = getUscisFeeAmount();

  const renderAmount = (amount: number | null, widthClass = "w-24") => {
    if (amount == null) {
      return (
        <span className="inline-block relative overflow-hidden rounded">
          <span className={`inline-block h-4 ${widthClass} bg-gray-200`} />
          <span className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent" />
        </span>
      );
    }
    return formatCurrency(amount);
  };

  const priceMarker = (
    icon: React.ReactNode,
    label: string,
    colorClass: string
  ) => (
    <span className={`inline-flex items-center gap-1.5 ${colorClass}`}>
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-current/10">
        {icon}
      </span>
      <span>{label}</span>
    </span>
  );

  const FeeBreakdown = ({ includeUscis }: { includeUscis: boolean }) => {
    const total =
      packageFee == null || (includeUscis && uscisFeeAmount == null)
        ? null
        : packageFee + (includeUscis ? uscisFeeAmount ?? 0 : 0);

    return (
      <div className="mb-5 rounded-xl border border-[#E5E7EB] bg-[#F8FAFC] p-4 text-[#1B365D]">
        {selectedPackage?.name ? (
          <p className="mb-2 text-[12px] font-semibold text-[#64748B]">
            {selectedPackage.name}
          </p>
        ) : null}
        {includeUscis ? (
          <>
            <p className="text-[18px] font-bold flex flex-wrap items-center gap-x-2 gap-y-1">
              {priceMarker(
                <FaBolt className="h-3 w-3 text-emerald-600" aria-hidden />,
                `${renderAmount(total, "w-28")} Now`,
                "text-[#1B365D]"
              )}
            </p>
            <p className="mt-1 text-[13px] text-[#64748B]">
              {priceMarker(
                <FaBolt className="h-3 w-3 text-emerald-600" aria-hidden />,
                `${renderAmount(packageFee, "w-20")}`,
                "text-[#1B365D]"
              )}{" "}
              +{" "}
              {priceMarker(
                <FaClock className="h-3 w-3 text-amber-500" aria-hidden />,
                `${renderAmount(uscisFeeAmount, "w-20")}`,
                "text-[#1B365D]"
              )}
            </p>
          </>
        ) : (
          <p className="text-[18px] font-bold flex flex-wrap items-center gap-x-2 gap-y-1">
            {priceMarker(
              <FaBolt className="h-3 w-3 text-emerald-600" aria-hidden />,
              `${renderAmount(packageFee, "w-24")} Now`,
              "text-[#1B365D]"
            )}
            <span>,</span>
            {priceMarker(
              <FaClock className="h-3 w-3 text-amber-500" aria-hidden />,
              `${renderAmount(uscisFeeAmount, "w-24")} Later`,
              "text-[#1B365D]"
            )}
          </p>
        )}
      </div>
    );
  };

  useEffect(() => {
    const fetchApplication = async () => {
      if (!id) {
        setError("Application ID is required");
        return;
      }

      // Prevent multiple simultaneous calls
      if (fetchingRef.current) {
        return;
      }

      fetchingRef.current = true;

      try {
        const app = await getApplication(parseInt(id));
        setApplication(app);

        const hasComputedUscisFee =
          app.uscisFee?.breakdown?.total !== null &&
          app.uscisFee?.breakdown?.total !== undefined
            ? true
            : app.uscisFee?.amount !== null &&
              app.uscisFee?.amount !== undefined;

        // Check if feeComputation is stored in sessionStorage (from UscisFeeQuestionnaire component)
        const storedFeeComputation = id
          ? sessionStorage.getItem(`uscis_fee_computation_${id}`)
          : null;
        if (storedFeeComputation) {
          try {
            const parsedFeeComputation: UscisFeeComputation =
              JSON.parse(storedFeeComputation);
            setFeeComputation(parsedFeeComputation);
            // Clear the stored feeComputation after reading
            sessionStorage.removeItem(`uscis_fee_computation_${id}`);
          } catch (e) {
            console.warn("Failed to parse stored fee computation:", e);
          }
        }

        // Fetch questionnaire only if stage is uscis_fee_questionnaire (questionnaire not yet submitted)
        // Once questionnaire is submitted, stage moves to uscis_fee_decision and fee is computed
        if (
          app.stage === "uscis_fee_questionnaire" &&
          !hasComputedUscisFee &&
          !questionnaireFetchedRef.current &&
          !questionnaire
        ) {
          questionnaireFetchedRef.current = true;
          try {
            const questionnaireData = await getUscisFeeQuestionnaire(
              parseInt(id)
            );
            if (
              questionnaireData &&
              questionnaireData.questions &&
              questionnaireData.questions.length > 0
            ) {
              setQuestionnaire(questionnaireData);
              setShowQuestionnaire(true);
            }
          } catch (questionnaireErr: any) {
            console.warn("Could not fetch questionnaire:", questionnaireErr);
            // Reset the ref so we can try again if needed
            questionnaireFetchedRef.current = false;
          }
        }
      } catch (err: any) {
        console.error("Error fetching application:", err);
        setError(
          err?.response?.data?.message ||
            "Failed to load application. Please try again."
        );
      } finally {
        fetchingRef.current = false;
      }
    };

    fetchApplication();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!application?.packageId) {
      setSelectedPackage(null);
      return;
    }

    let cancelled = false;

    const fetchPackage = async () => {
      try {
        setPackageLoading(true);
        const pkg = await getPackageById(application.packageId as number);
        if (!cancelled) setSelectedPackage(pkg);
      } catch (err) {
        console.warn("Could not load selected package:", err);
        if (!cancelled) setSelectedPackage(null);
      } finally {
        if (!cancelled) setPackageLoading(false);
      }
    };

    fetchPackage();

    return () => {
      cancelled = true;
    };
  }, [application?.packageId]);

  const handleDecision = async (optIn: boolean) => {
    if (!id) {
      setError("Application ID is required");
      return;
    }

    if (!application?.packageId) {
      setError("Package not selected. Please select a package first.");
      return;
    }

    // Prevent double-click for Pay Later option
    if (!optIn && payLaterProcessingRef.current) {
      return;
    }

    if (!optIn) {
      payLaterProcessingRef.current = true;
    }

    setSuccessMessage(null);
    setError(null);

    // If questionnaire is currently showing, validate and submit it first
    if (showQuestionnaire && questionnaire) {
      // Validate questionnaire first
      if (!validateForm()) {
        return;
      }

      // Convert answers to answer array format
      const answerArray: UscisFeeAnswer[] = questionnaire.questions.map(
        (question) => ({
          key: question.key,
          value:
            answers[question.key] ?? (question.type === "boolean" ? false : ""),
        })
      );

      try {
        setSubmitting(true);
        // Submit questionnaire immediately - backend will compute the fee
        const result = await submitUscisFeeQuestionnaire(
          parseInt(id),
          answerArray
        );

        // Update application state
        setApplication(result.application);

        // Store feeComputation if available (application.uscisFee might be null but feeComputation has the computed fee)
        if (result.feeComputation) {
          setFeeComputation(result.feeComputation);
        }

        // Hide questionnaire
        setShowQuestionnaire(false);
        setAnswers({});
        setErrors({});

        // Now proceed with the decision
        await submitDecision(optIn, answerArray);
      } catch (err: any) {
        console.error("Error submitting questionnaire:", err);
        setError(
          err?.response?.data?.message ||
            "Failed to submit questionnaire. Please try again."
        );
        setSubmitting(false);
        if (!optIn) {
          payLaterProcessingRef.current = false;
        }
      }
      return;
    }

    // Questionnaire has already been submitted, fee is computed
    // Just proceed with the decision (no need to pass answers, they're already saved)
    // Pass empty array since answers are already submitted
    await submitDecision(optIn, []);
  };

  const createCheckoutForPackageOnly = async () => {
    if (!id || !application?.packageId) {
      setError("Application ID and package ID are required");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Create checkout session directly - backend knows uscisFeeOptIn is false
      // so it will only charge for package fees, not USCIS fees
      const successUrl = `${window.location.origin}/dashboard?session_id={CHECKOUT_SESSION_ID}&payment_status=success`;
      const cancelUrl = `${window.location.origin}/dashboard?payment_status=cancel`;

      const session = await createCheckoutSession(
        parseInt(id),
        application.packageId,
        successUrl,
        cancelUrl
      );

      // Redirect to checkout
      window.location.href = session.sessionUrl;
    } catch (err: any) {
      console.error("Error creating checkout for package only:", err);
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to create checkout session. Please try again.";

      setError(errorMessage);
      setSubmitting(false);
    }
  };

  const submitDecisionWithPackageCheckout = async (
    optIn: boolean,
    answerArray: UscisFeeAnswer[]
  ) => {
    if (!id || !application?.packageId) {
      setError("Application ID and package ID are required");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Double-check the current stage before calling setUscisFeeDecision
      // Refresh application state to ensure we have the latest stage
      const currentApp = await getApplication(parseInt(id));

      // If stage is already package_selected, skip setUscisFeeDecision and create checkout directly
      if (currentApp.stage === "package_selected") {
        setApplication(currentApp);
        await createCheckoutForPackageOnly();
        return;
      }

      // Application is in uscis_fee_decision stage, save the decision with questionnaire answers
      // Save the USCIS fee decision with questionnaire answers (optIn: false)
      // This saves the questionnaire data but doesn't compute USCIS fees
      const result: UscisFeeDecisionResponse = await setUscisFeeDecision(
        parseInt(id),
        optIn, // false for "Pay Later"
        answerArray, // Save questionnaire answers
        undefined, // No success URL needed
        undefined // No cancel URL needed
      );

      // Update application state
      setApplication(result.application);

      // Close questionnaire
      setShowQuestionnaire(false);
      setAnswers({});
      setErrors({});

      // Now create checkout session for package fees only (without USCIS fees)
      // Backend will check uscisFeeOptIn and only charge package fees
      const successUrl = `${window.location.origin}/dashboard?session_id={CHECKOUT_SESSION_ID}&payment_status=success`;
      const cancelUrl = `${window.location.origin}/dashboard?payment_status=cancel`;

      const session = await createCheckoutSession(
        parseInt(id),
        application.packageId,
        successUrl,
        cancelUrl
      );

      // Redirect to checkout
      window.location.href = session.sessionUrl;
    } catch (err: any) {
      console.error("Error in submitDecisionWithPackageCheckout:", err);

      // If error is about stage validation, try to create checkout directly
      const errorMessage = err?.response?.data?.message || err?.message || "";
      if (
        errorMessage.includes("Must be in 'uscis_fee_decision' stage") ||
        errorMessage.includes("package_selected")
      ) {
        // Stage is already package_selected, just create checkout
        await createCheckoutForPackageOnly();
        return;
      } else {
        setError(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            err?.message ||
            "Failed to process payment. Please try again."
        );
        setSubmitting(false);
      }
    }
  };

  const validateForm = (): boolean => {
    if (!questionnaire) return false;

    const newErrors: Record<string, string> = {};

    questionnaire.questions.forEach((question) => {
      if (question.required) {
        const value = answers[question.key];
        if (value === undefined || value === null || value === "") {
          newErrors[question.key] = "This field is required";
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submitDecision = async (
    optIn: boolean,
    answerArray: UscisFeeAnswer[]
  ) => {
    if (!id) {
      setError("Application ID is required");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Check current application stage before calling setUscisFeeDecision
      // Refresh application state to ensure we have the latest stage
      const currentApp = await getApplication(parseInt(id));
      setApplication(currentApp);

      // If stage is already package_selected and optIn is false, skip setUscisFeeDecision
      // Just create checkout directly (decision was already made)
      if (currentApp.stage === "package_selected" && !optIn) {
        // For "Pay Later" when stage is package_selected, create checkout for package only
        if (currentApp.packageId) {
          await createCheckoutForPackageOnly();
        } else {
          setError("Package not found");
          setSubmitting(false);
        }
        return;
      }

      // Questionnaire should already be submitted at this point (fee is computed)
      // If stage is still uscis_fee_questionnaire, something went wrong
      if (currentApp.stage === "uscis_fee_questionnaire") {
        setError(
          "Questionnaire must be submitted before making a decision. Please complete the questionnaire first."
        );
        setSubmitting(false);
        return;
      }

      // Construct success and cancel URLs for Stripe checkout session
      // These URLs are required by the backend when optIn is true
      const successUrl = optIn
        ? `${window.location.origin}/dashboard?session_id={CHECKOUT_SESSION_ID}&payment_status=success`
        : undefined;
      const cancelUrl = optIn
        ? `${window.location.origin}/dashboard?payment_status=cancel`
        : undefined;

      // Refresh application state one more time to ensure we have the latest stage
      const finalApp = await getApplication(parseInt(id));
      setApplication(finalApp);

      console.log("Setting USCIS fee decision:", {
        applicationId: id,
        optIn,
        answers: answerArray,
        successUrl,
        cancelUrl,
        currentStage: finalApp.stage,
      });

      const result: UscisFeeDecisionResponse = await setUscisFeeDecision(
        parseInt(id),
        optIn,
        answerArray,
        successUrl,
        cancelUrl
      );

      console.log("USCIS fee decision response:", result);
      console.log("Updated application stage:", result.application.stage);
      console.log("Fee computation:", result.feeComputation);
      console.log("Checkout URL:", result.checkoutUrl);

      // Update application state with the returned application (includes computed fee)
      setApplication(result.application);

      // Close questionnaire and show computed fee
      setShowQuestionnaire(false);
      setAnswers({});
      setErrors({});

      if (optIn) {
        // Fee has been computed and saved
        // If checkout URL is returned, redirect immediately to Stripe checkout
        if (result.checkoutUrl) {
          // Redirect to Stripe checkout immediately
          window.location.href = result.checkoutUrl;
          return; // Exit early since we're redirecting
        } else {
          // If no checkout URL was returned, create checkout session manually
          // This might happen if backend didn't create the session
          if (application?.packageId) {
            const successUrl = `${window.location.origin}/dashboard?session_id={CHECKOUT_SESSION_ID}&payment_status=success`;
            const cancelUrl = `${window.location.origin}/dashboard?payment_status=cancel`;

            try {
              const session = await createCheckoutSession(
                parseInt(id),
                application.packageId,
                successUrl,
                cancelUrl
              );
              // Redirect to checkout
              window.location.href = session.sessionUrl;
              return; // Exit early since we're redirecting
            } catch (checkoutErr: any) {
              console.error("Error creating checkout session:", checkoutErr);
              setError(
                checkoutErr?.response?.data?.message ||
                  "Failed to create checkout session. Please try again."
              );
              setSubmitting(false);
              return;
            }
          } else {
            setError("Package not found. Cannot proceed to checkout.");
            setSubmitting(false);
            return;
          }
        }
      } else {
        setSuccessMessage(
          "You can continue your application and pay the USCIS fee later."
        );

        // Navigate based on application stage for "Pay Later"
        if (result.application.stage === "uscis_fee_ready") {
          navigate(`/application/${id}/uscis-fee`, {
            replace: true,
            state: { refresh: Date.now(), showDecision: false },
          });
        } else if (result.application.stage === "uscis_fee_decision") {
          navigate(`/application/${id}/uscis-fee`, {
            replace: true,
            state: { refresh: Date.now(), showDecision: true },
          });
        }
      }
    } catch (err: any) {
      console.error("Error setting USCIS fee decision:", err);
      console.error("Error details:", {
        message: err?.message,
        response: err?.response?.data,
        status: err?.response?.status,
        statusText: err?.response?.statusText,
      });

      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to save decision. Please try again.";

      setError(errorMessage);
    } finally {
      setSubmitting(false);
      if (!optIn) {
        payLaterProcessingRef.current = false;
      }
    }
  };

  const handleSubmitQuestionnaire = async () => {
    if (!questionnaire || !id) return;

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setLoadingFee(true);
      setError(null);

      const answerArray: UscisFeeAnswer[] = questionnaire.questions.map(
        (question) => ({
          key: question.key,
          value:
            answers[question.key] ?? (question.type === "boolean" ? false : ""),
        })
      );

      // Submit questionnaire immediately - backend will compute the fee
      const result = await submitUscisFeeQuestionnaire(
        parseInt(id),
        answerArray
      );

      // Update application state
      setApplication(result.application);

      // Store feeComputation if available (application.uscisFee might be null but feeComputation has the computed fee)
      if (result.feeComputation) {
        setFeeComputation(result.feeComputation);
      }

      // Hide questionnaire and show decision page
      setShowQuestionnaire(false);
      setAnswers({});
      setErrors({});
    } catch (err: any) {
      console.error("Error submitting questionnaire:", err);
      setError(
        err?.response?.data?.message ||
          "Failed to submit questionnaire. Please try again."
      );
    } finally {
      setSubmitting(false);
      setLoadingFee(false);
    }
  };

  const handleAnswerChange = (
    key: string,
    value: boolean | string | number
  ) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
    // Clear error for this field when user starts typing
    if (errors[key]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[key];
        return newErrors;
      });
    }
  };

  const renderQuestion = (question: UscisFeeQuestion) => {
    const value = answers[question.key];
    const hasError = !!errors[question.key];

    switch (question.type) {
      case "boolean":
        return (
          <div>
            <label className="block text-[#1B365D] font-medium mb-2">
              {question.questionText}
              {question.required && (
                <span className="text-red-500 ml-1">*</span>
              )}
            </label>
            <div className="flex items-center space-x-6">
              <label className="inline-flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name={question.key}
                  checked={value === true}
                  onChange={() => handleAnswerChange(question.key, true)}
                />
                <span className="text-[#1B365D]">Yes</span>
              </label>
              <label className="inline-flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name={question.key}
                  checked={value === false}
                  onChange={() => handleAnswerChange(question.key, false)}
                />
                <span className="text-[#1B365D]">No</span>
              </label>
            </div>
            {question.helpText && (
              <p className="text-sm text-gray-600 mt-2 flex items-start">
                <FaInfoCircle className="mr-2 mt-0.5 flex-shrink-0" />
                {question.helpText}
              </p>
            )}
            {hasError && (
              <p className="text-sm text-red-600 mt-1">
                {errors[question.key]}
              </p>
            )}
          </div>
        );

      case "select":
        return (
          <div>
            <label className="block text-[#1B365D] font-medium mb-2">
              {question.questionText}
              {question.required && (
                <span className="text-red-500 ml-1">*</span>
              )}
            </label>
            <select
              value={(value as string) || ""}
              onChange={(e) => handleAnswerChange(question.key, e.target.value)}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#00B894] focus:border-[#00B894] ${
                hasError ? "border-red-500" : "border-gray-300"
              }`}
            >
              <option value="">Select an option</option>
              {question.options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {question.helpText && (
              <p className="text-sm text-gray-600 mt-1 flex items-start">
                <FaInfoCircle className="mr-2 mt-0.5 flex-shrink-0" />
                {question.helpText}
              </p>
            )}
            {hasError && (
              <p className="text-sm text-red-600 mt-1">
                {errors[question.key]}
              </p>
            )}
          </div>
        );

      case "number":
        return (
          <div>
            <label className="block text-[#1B365D] font-medium mb-2">
              {question.questionText}
              {question.required && (
                <span className="text-red-500 ml-1">*</span>
              )}
            </label>
            <input
              type="number"
              value={(value as number) || ""}
              onChange={(e) =>
                handleAnswerChange(
                  question.key,
                  e.target.value ? parseFloat(e.target.value) : ""
                )
              }
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#00B894] focus:border-[#00B894] ${
                hasError ? "border-red-500" : "border-gray-300"
              }`}
            />
            {question.helpText && (
              <p className="text-sm text-gray-600 mt-1 flex items-start">
                <FaInfoCircle className="mr-2 mt-0.5 flex-shrink-0" />
                {question.helpText}
              </p>
            )}
            {hasError && (
              <p className="text-sm text-red-600 mt-1">
                {errors[question.key]}
              </p>
            )}
          </div>
        );

      case "text":
      default:
        return (
          <div>
            <label className="block text-[#1B365D] font-medium mb-2">
              {question.questionText}
              {question.required && (
                <span className="text-red-500 ml-1">*</span>
              )}
            </label>
            <input
              type="text"
              value={(value as string) || ""}
              onChange={(e) => handleAnswerChange(question.key, e.target.value)}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#00B894] focus:border-[#00B894] ${
                hasError ? "border-red-500" : "border-gray-300"
              }`}
            />
            {question.helpText && (
              <p className="text-sm text-gray-600 mt-1 flex items-start">
                <FaInfoCircle className="mr-2 mt-0.5 flex-shrink-0" />
                {question.helpText}
              </p>
            )}
            {hasError && (
              <p className="text-sm text-red-600 mt-1">
                {errors[question.key]}
              </p>
            )}
          </div>
        );
    }
  };

  if (error && !application) {
    return (
      <div className="page_main page_main-after-login mt-[100px] pt-[40px] bg-[#F7F8FA] pb-[100px]">
        <div className="mx-auto w-full xl:max-w-[65%] md:max-w-[90%] max-w-[95%] lg:p-[50px] p-[20px] rounded-2xl border border-[#E5E7EB] bg-white">
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-[#1B365D] mb-2">
              Error Loading Application
            </h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-[#F4A261] text-[#1B365D] font-semibold px-6 py-3 rounded-[50px] hover:bg-[#F29449]"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show questionnaire if it needs to be filled (before decision page)
  if (showQuestionnaire && questionnaire) {
    const canSubmit = questionnaire.questions.every((q) => {
      if (!q.required) return true;
      const v = answers[q.key];
      if (q.type === "boolean") return typeof v === "boolean";
      return v !== undefined && v !== null && v !== "";
    });

    return (
      <div className="page_main page_main-after-login mt-[100px] pt-[40px] bg-[#F7F8FA] pb-[100px]">
        <div className="mx-auto w-full xl:max-w-[65%] md:max-w-[90%] max-w-[95%] lg:p-[50px] p-[20px] rounded-2xl border border-[#E5E7EB] bg-white">
          <h1 className="font-bold text-center mb-4 lg:text-[40px] md:text-[30px] text-[26px]">
            {questionnaire.title}
          </h1>
          {questionnaire.description && (
            <p className="text-center mb-8 text-[#1B365DCC]">
              {questionnaire.description}
            </p>
          )}

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmitQuestionnaire();
            }}
            className="space-y-6"
          >
            <PovertyGuidelineHelp />

            {questionnaire.questions.map((question) => (
              <div
                key={question.key}
                className="border-b border-gray-200 pb-6 last:border-b-0"
              >
                {renderQuestion(question)}
              </div>
            ))}

            <div className="flex justify-between mt-8">
              <button
                type="button"
                onClick={() => {
                  setShowQuestionnaire(false);
                  setAnswers({});
                  setErrors({});
                }}
                className="px-6 py-3 rounded-[50px] text-[#1B365D] font-semibold border border-[#1B365D] hover:bg-gray-50"
              >
                Back
              </button>
              <button
                type="submit"
                className="flex items-center justify-center px-6 py-3 rounded-[50px] text-white font-semibold"
                style={buttonStyleSecondarySm22}
                disabled={submitting || !canSubmit}
              >
                {submitting ? <ButtonLoadingLabel label="Submitting" /> : "Continue"}{" "}
                {!submitting && <FaArrowRight className="ms-4" />}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Show decision options
  return (
    <div className="page_main page_main-after-login mt-[100px] pt-[40px] bg-[#F7F8FA] pb-[100px]">
      <div className="mx-auto w-full xl:max-w-[65%] md:max-w-[90%] max-w-[95%] lg:p-[50px] p-[20px] rounded-2xl border border-[#E5E7EB] bg-white">
        <h1 className="font-bold text-center mb-4 lg:text-[40px] md:text-[30px] text-[26px]">
          Apply with confidence: guaranteed approval or your money back
        </h1>
        <p className="text-center mb-8 text-[#1B365DCC]">
          Reduce your risk of application rejection or denial by 25% with
          step-by-step guidance, live support, and quality reviews by an
          experienced team.
        </p>
        <p className="flex items-center mb-10">
          Select USCIS fee option{" "}
          <FaCircleCheck className="ml-3 text-[#00B894]" />
        </p>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Pay Later Option */}
          <div className="border border-[#D1D5DB] rounded-2xl p-[25px]">
            <p className="flex items-center mb-4">
              <FaClock className="text-[28px] me-3 text-amber-500" /> Pay USCIS fees later
            </p>
            <p className="text-[12px] text-[#6B7280] mb-4">HOW IT WORKS</p>
            <p className="text-[14px] text-[#1B365DCC] mb-4">
              Pay by credit card, or mail us a check to send with your
              application. You'll need to pay USCIS when we ship your
              application.
            </p>
            <FeeBreakdown includeUscis={false} />
            <button
              className="w-full flex justify-center items-center"
              style={buttonStyleSm2}
              onClick={() => handleDecision(false)}
              disabled={loading || submitting}
            >
              {loading || submitting ? (
                <ButtonLoadingLabel label="Processing" />
              ) : (
                "Select"
              )}{" "}
              {!loading && !submitting && <FaArrowRight className="ms-4" />}
            </button>
          </div>

          {/* Pay Now Option (Recommended) */}
          <div className="border-2 border-[#00B894] rounded-2xl p-[25px] relative">
            <label className="text-[12px] bg-[#00B894] text-white py-1 px-3 rounded-[20px] absolute top-[-15px] left-1/2 transform -translate-x-1/2">
              RECOMMENDED
            </label>
            <p className="flex items-center mb-4">
              <FaBolt className="text-[28px] me-3 text-emerald-600" /> Pay USCIS fees now
            </p>
            <p className="text-[12px] text-[#6B7280] mb-4">&nbsp;</p>
            <p className="text-[14px] text-[#1B365DCC] mb-4">
              Pay via credit or debit, with an option to pay over 12 months.
              We'll write the check to USCIS for you.
            </p>
            <FeeBreakdown includeUscis />
            <button
              className="w-full flex justify-center items-center"
              style={buttonStyleSecondarySm22}
              onClick={() => handleDecision(true)}
              disabled={loading || submitting}
            >
              {loading || submitting ? (
                <ButtonLoadingLabel label="Loading" />
              ) : (
                "Select"
              )}{" "}
              {!loading && !submitting && <FaArrowRight className="ms-4" />}
            </button>
            <p className="text-[12px] text-[#6B7280] text-center mt-4">
              Includes your package fee and USCIS fee in one checkout.
            </p>
          </div>
        </div>

        {successMessage && (
          <div className="mt-8 p-4 bg-[#ecfdf5] border border-[#10b981] rounded-xl text-sm text-[#065f46]">
            {successMessage}
          </div>
        )}

        {application?.stage === "uscis_fee_ready" && (
          <div className="mt-6 flex justify-center">
            <button
              onClick={() => {
                // Check if we have a stored checkout URL
                const checkoutUrl = id
                  ? sessionStorage.getItem(`checkoutUrl_${id}`)
                  : null;
                if (checkoutUrl) {
                  // Redirect to Stripe checkout
                  window.location.href = checkoutUrl;
                } else {
                  // Navigate to payment page
                  navigate(`/application/${id}/payment`);
                }
              }}
              className="bg-[#1B365D] text-white font-semibold px-8 py-3 rounded-full hover:bg-[#152849]"
            >
              Continue to Payment
            </button>
          </div>
        )}

        <p className="text-[12px] text-center text-[#6B7280] mt-10">
          A portion of your payment goes to the independent attorneys who review
          your application, as provided in the Limited Scope Agreement.
        </p>
        <p className="text-[12px] text-center text-[#6B7280]">
          Government filing fees not included.
        </p>
      </div>
    </div>
  );
};

export default UscisFeeDecision;
